import { useCallback, useEffect, useState } from "react";

interface UseNavigationGuardProps {
  hasUnsavedChanges: boolean;
  onConfirmNavigate: (target: string) => void;
}

export const useNavigationGuard = ({
  hasUnsavedChanges,
  onConfirmNavigate,
}: UseNavigationGuardProps): {
  attemptNavigate: (target: string) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  showAlert: boolean;
} => {
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);

  const attemptNavigate = useCallback(
    (target: string) => {
      if (hasUnsavedChanges) {
        setPendingTarget(target);
        setShowAlert(true);
      } else {
        onConfirmNavigate(target);
      }
    },
    [hasUnsavedChanges, onConfirmNavigate]
  );

  const confirmNavigation = useCallback(() => {
    if (pendingTarget) {
      onConfirmNavigate(pendingTarget);
      setPendingTarget(null);
      setShowAlert(false);
    }
  }, [pendingTarget, onConfirmNavigate]);

  const cancelNavigation = useCallback(() => {
    setPendingTarget(null);
    setShowAlert(false);
  }, []);

  // Block tab close / refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    attemptNavigate,
    confirmNavigation,
    cancelNavigation,
    showAlert,
  };
};
